/**
 * PageContainer — 10º componente canônico do design system (rodada
 * LAYOUT-CANONICO).
 *
 * Motivo (smoke do Matheus + Cowork no beta 9a39592): comparando Clientes ×
 * Funil × Ajuda lado a lado, **cada tela começava numa coordenada X diferente
 * e tinha uma largura diferente** — não pareciam o mesmo sistema. Medido em
 * 1568px: Clientes começava em 288px (colada na sidebar, sem respiro), Funil em
 * 312px, Ajuda em 448px com um bloco de 1024px (270px de vazio à esquerda).
 * Causa: cada tela inventava o próprio wrapper — `<main>` do AppShell não tem
 * padding nenhum. É o mesmo vício que fez o tooltip ser reimplementado tela a
 * tela antes do Tooltip canônico.
 *
 * A régua, agora única:
 *   - padding horizontal 16px (mobile) → 32px (md+)
 *   - padding vertical 24px (mobile) → 40px (md+)
 *   - variante `padrao`: largura FLUIDA (L-1) — ocupa o monitor inteiro. O teto
 *     de 1440px morreu: a 1860px sobravam ~450px de área morta à direita
 *   - variante `full`: idem, mas para quem rola na horizontal (Kanban do Funil
 *     e dos Leads), que além disso não pode ser espremido
 *   - variante `leitura`: a ÚNICA com teto — texto corrido (Ajuda), com medida
 *     de linha de ~75 caracteres, porque linha quilométrica cansa
 *
 * **Só existem essas três variantes.** Nada de `className` solto por fora: se
 * uma tela precisa de outra margem, ou ela cabe numa das três variantes, ou é
 * bug de review. Margem/padding inventado em tela é o que esta rodada matou.
 *
 * O atributo `data-page-container` fica no DOM de propósito: é o que o gate
 * visual usa para provar que nenhuma tela ficou com wrapper próprio.
 */
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { useCasca } from '@/pages/admin/configuracoes/casca-contrato';

export type PageContainerVariante = 'padrao' | 'full' | 'leitura';

/**
 * A régua, exportada para a CASCA (AppShell/AdminLayout) alinhar com o conteúdo
 * sem repetir valores mágicos. Tela nenhuma deve importar isto — tela usa
 * `<PageContainer>`.
 */
export const REGUA_PADDING_X = 'px-4 md:px-8';
/**
 * L-1 (LARGURA-E-ESCALA): o teto de 1440px MORREU.
 *
 * Ele fazia sentido em 1280 e virou desperdício em monitor grande: no smoke do
 * Matheus a 1860px sobravam **~450px de área morta à direita** em várias telas
 * — tabela densa espremida com o monitor vazio ao lado. Largura fluida, com a
 * mesma coordenada X inicial que a rodada LAYOUT-CANONICO fixou (o ponto de
 * partida continua idêntico entre telas; o que muda é até onde vai).
 *
 * ⚠️ O efeito colateral a vigiar: formulário de coluna única esticado de ponta
 * a ponta em 1920. A correção é **o campo** ganhar largura máxima própria — nunca
 * o container voltar a ter teto. Container com teto conserta uma tela e estraga
 * as outras dez.
 */
export const REGUA_LARGURA = 'w-full';
/**
 * A ÚNICA exceção: texto corrido (artigo da Ajuda e afins).
 *
 * Linha longa demais cansa — o olho perde o começo da linha seguinte. ~75
 * caracteres é a medida de leitura clássica, e `ch` a expressa na unidade certa:
 * ela acompanha a fonte, então a escala tipográfica nova (L-2) não desregula a
 * medida de linha.
 *
 * É **variante do componente**, não CSS solto na tela — foi CSS solto em tela
 * que criou o problema que o `PageContainer` veio resolver.
 */
export const REGUA_LEITURA = 'w-full max-w-[75ch]';

export function PageContainer({
  variante = 'padrao',
  alturaCheia = false,
  daCasca = false,
  children,
  'data-testid': dataTestId,
}: {
  variante?: PageContainerVariante;
  /**
   * Para telas cujo conteúdo rola DENTRO dele (os Kanbans do Funil e dos
   * Leads): o container passa a ocupar a altura da janela e vira `flex-col`,
   * então o filho com `flex-1 min-h-0` rola sozinho. Sem isso o container de
   * `overflow-x` cresce com a coluna mais alta e a barra de rolagem horizontal
   * "flutua no meio" da tela (regra registrada no BUGS.md).
   *
   * Não é uma terceira variante: a régua horizontal continua sendo uma só —
   * isto é comportamento de rolagem, e mora aqui para o cálculo de altura não
   * voltar a ser copiado tela a tela.
   */
  alturaCheia?: boolean;
  /**
   * Marca ESTE container como o da própria casca da Configurações.
   *
   * Mesma necessidade que o `daCasca` do `PageHeader`, e o mesmo erro que eu
   * cometi lá: o `CascaProvider` envolve a casca inteira, então sem esta marca o
   * container **da casca** também se silenciava — a página perdia o padding
   * vertical (breadcrumb colado na TopBar, ~55px do topo em vez de ~160px) e a
   * régua horizontal.
   *
   * Só a casca usa. Tela de conteúdo nunca passa isto.
   */
  daCasca?: boolean;
  children: ReactNode;
  'data-testid'?: string;
}) {
  // DENTRO da casca da Configurações a régua já foi aplicada pelo container da
  // própria casca. Repetir aqui daria padding em dobro e afastaria o conteúdo
  // do menu de seções — a tela embutida entrega só o conteúdo (K-4).
  const { dentroDaCasca } = useCasca();
  const silenciar = dentroDaCasca && !daCasca;

  return (
    <div
      data-page-container={variante}
      data-testid={dataTestId}
      className={cn(
        'w-full',
        // C6-A #5 (08/08/2026): com `alturaCheia` o padding de BAIXO vira faixa
        // morta. Medido: o vão sob a barra horizontal era de exatos 40px em
        // 1366×768, 1280×720 e 1920×1080 — o mesmo valor do `py-10`. Como o
        // container tem `overflow-hidden` e altura fixa, esse padding não
        // "empurra" nada: ele só reserva um vazio que o quadro nunca alcança.
        //
        // O de CIMA fica: é o respiro entre a TopBar e o cabeçalho da página, e
        // tirá-lo colaria o breadcrumb na casca (o erro que a Configurações já
        // cometeu com o `daCasca`).
        !silenciar && !alturaCheia && 'py-6 md:py-10',
        !silenciar && alturaCheia && 'pt-6 md:pt-10 pb-0',
        !silenciar && REGUA_PADDING_X,
        variante === 'padrao' && REGUA_LARGURA,
        variante === 'leitura' && REGUA_LEITURA,
        // B-02 (HOTFIX-ROLAGEM): a conta da altura fecha com a casca pelas
        // variáveis que o AppShell publica (--igm-topbar-h / --igm-tabbar-h) —
        // nada de número copiado no olho, que foi o que deixou 9px sobrando e
        // a PÁGINA com uma segunda barra vertical competindo com a da coluna.
        // O `overflow-hidden` garante que quem rola é só quem foi feito para
        // rolar (a coluna do Kanban), nunca a página.
        // C6-A #6 (08/08/2026) — "altura mínima com escape".
        //
        // Era `h-[...]` FIXO: o quadro valia exatamente o que sobrava da janela,
        // e em tela baixa encolhia até mostrar 1 card por coluna (medido: 1 card
        // em 1366×768 e em 1280×720; ZERO card inteiro em 375px).
        //
        // Agora é `min-h`: enquanto o quadro couber, nada muda — ele ocupa a
        // janela e quem rola é a coluna. Quando NÃO couber, o quadro para de
        // encolher no seu piso e é a PÁGINA que passa a rolar.
        //
        // O `max()` é o que faz o "escape": a altura é o MAIOR entre o piso do
        // quadro e o espaço que sobra da janela. Enquanto a janela ganha, nada
        // muda — quem rola é a coluna, e o `overflow-hidden` garante isso.
        // Quando o PISO ganha, o container fica mais alto que a janela e é a
        // PÁGINA que rola; o quadro para de encolher.
        alturaCheia &&
          'flex flex-col min-h-0 overflow-hidden ' +
            'h-[max(var(--igm-kanban-quadro-min),calc(100dvh-var(--igm-topbar-h)-var(--igm-rodape-mobile-h)))] ' +
            'md:h-[max(var(--igm-kanban-quadro-min),calc(100dvh-var(--igm-topbar-h)))]',
      )}
    >
      {children}
    </div>
  );
}
